import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { InvoiceService } from '../../features/invoice/services/invoice.service';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { VendorInvoiceService } from '../../features/vendor-invoice/services/vendor-invoice.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { PermissionService } from '../../shared/services/permission.service';

@Component({
  selector: 'app-vendor-invoice-details',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatTabsModule,
    MatMenuModule,
    MatIconModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './vendor-invoice-details.component.html',
  styleUrl: './vendor-invoice-details.component.scss',
})
export class VendorInvoiceDetailsComponent implements OnInit {
  selectedTabIndex = 0;
  invoiceId: any;
  invoiceData: any = {};
  allInvoices: any[] = [];
  journalData: any;
  public loading: boolean = true;
  private searchSubject = new Subject<string>();
  isAttacheFile = false;
  vat: any = 0;
  canAddBill$ = this.permissionService.hasPermission(
    ModuleIds.VendorInvoice,
    'Create'
  );
  canEditBill$ = this.permissionService.hasPermission(
    ModuleIds.VendorInvoice,
    'Edit'
  );
  canApproveReject$ = this.permissionService.hasPermission(
    ModuleIds.VendorInvoice,
    'Approve/Reject'
  );


  constructor(
    private router: Router,
    private invoiceService: InvoiceService,
    private route: ActivatedRoute,
    private vendorInvoiceService: VendorInvoiceService,
    private toastr: ToastrService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((queryParams) => {
      this.invoiceId = queryParams.get('invoiceId');
      this.fetchAllVendorInvoices()
    });

    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.fetchAllVendorInvoices(searchTerm)
      });
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  onViewDocument() {
    const url = this.invoiceData.billUrl;
    const token = localStorage.getItem('AUTH_TOKEN');
    fetch(url, {
        method: 'GET',
        headers: {
            'authorization': token ? `Bearer ${token}` : ''
        }
    }).then((response: any) => response.blob()).then((blob: any) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
    }).catch((error: any) => {
        console.error('Error:', error)
    });
}



  fetchAllVendorInvoices(search: string = '') {
    const input = {
      search: search
    };
    this.loading = true;
    this.vendorInvoiceService.getAllVendorInvoices(input).subscribe({
      next: (invoices) => {
        console.log(invoices);
        
        this.allInvoices = invoices?.bills || [];

        if (this.invoiceId) {
          const targetId = +this.invoiceId;
          const index = this.allInvoices.findIndex(
            (c: any) => c.id === targetId
          );
          this.selectedTabIndex = index !== -1 ? index : 0;
          this.invoiceData = this.allInvoices[this.selectedTabIndex] || {};
          this.getVendorInvoiceById(targetId); // ensure latest data
        } else {
          this.selectedTabIndex = 0;
          this.invoiceData = this.allInvoices[0] || {};
        }
      },
      error: (err) => {
        console.error('Error fetching invoices:', err);
      },
    });
  }

  getVendorInvoiceById(id: any) {
    this.vendorInvoiceService.listBillById(Number(id)).subscribe({
      next: (data) => {
        this.invoiceData = data;
        this.journalData = this.invoiceData.journalData;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  selectTab(index: number) {
    this.selectedTabIndex = index;
    this.invoiceData = this.allInvoices[index];
    this.journalData = this.invoiceData.journalData;
    if (this.invoiceData) {
      this.router.navigate(['vendor-invoice/vendor-invoice-details'], {
        queryParams: { invoiceId: this.invoiceData.id },
      });
    }
  }
  isEditableStatus(): boolean {
    const status = this.invoiceData?.billStatus;
    return status !== 'Approved' && status !== 'Rejected' && status !== 'Sent';
  }

  getDebitTotal(): number {
    return (
      this.invoiceData?.journalData?.journalEntries?.reduce(
        (sum: number, entry: any) => sum + (entry.debit || 0),
        0
      ) || 0
    );
  }

  getCreditTotal(): number {
    return (
      this.invoiceData?.journalData?.journalEntries?.reduce(
        (sum: number, entry: any) => sum + (entry.credit || 0),
        0
      ) || 0
    );
  }

  navigateToEditPage(): void {
    const selectedInvoice = this.allInvoices[this.selectedTabIndex];
    const invoiceId = selectedInvoice.id;
    this.router.navigate([`vendor-invoice/vendor-invoice-edit/${invoiceId}`]);
  }

  onRemoveInvoice() {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Confirm Cancel',
        message: 'Are you sure you want to cancel this invoice?',
        confirmText: 'Confirm',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.removeInvoice(this.invoiceId);
      }
    });
  }

  private removeInvoice(invoiceId: number): void {
    this.vendorInvoiceService.removeBill(Number(invoiceId)).subscribe({
      next: () => {
        this.toastr.success('Bill removed successfully.');
        this.router.navigate([`vendor-invoice`]);
      },
      error: (error) => {
        this.toastr.error(
          error.message || 'Failed to remove bill. Please try again.'
        );
      },
    });
  }

  handleApproval(isApproved: boolean, invoiceId: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: isApproved ? 'Confirm Approval' : 'Confirm Rejection',
        message: isApproved
          ? 'Are you sure you want to approve this bill?'
          : 'Are you sure you want to reject this bill?',
        confirmText: 'Confirm',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.vendorInvoiceService
          .approveOrRejectBill(Number(invoiceId), isApproved)
          .subscribe({
            next: (res) => {
              console.log(res);
              this.toastr.success('Bill status updated.');
              this.fetchAllVendorInvoices();
              this.getVendorInvoiceById(this.invoiceId);
              if(isApproved){
                this.generatePdf(invoiceId)
              }
            },
            error: (err) => {
              console.error('Error updating bill status:', err);
            },
          });
      }
    });
  }


  generatePdf(invoiceId: any) {
    this.vendorInvoiceService.generateBillPdf(Number(invoiceId)).subscribe({
      next: (res) => {
        this.fetchAllVendorInvoices();
        this.getVendorInvoiceById(this.invoiceId);
        this.toastr.success('Vendor Invoice pdf successfully generated');
      },
      error: (err) => {
        console.log('Error in generate pdf', err);
      },
    });
  }

  readonly dialog = inject(MatDialog);

  markAsSent() {
    this.invoiceService.markInvoiceAsSent(Number(this.invoiceId)).subscribe({
      next: (res) => {
        this.fetchAllVendorInvoices();
        this.getVendorInvoiceById(this.invoiceId);
      },
      error: (err) => {
        console.error('Error updating invoice status:', err);
      },
    });
  }
}
